import { useRef, useEffect, useState } from 'react';
import PalmerSectionLabel from './PalmerSectionLabel';

const testimonials = [
  {
    quote: '"Nova AI Digital elevated every layer of our brand\'s online presence. From motion details to structural layout, every piece felt crafted and intentional. The site not only looked beautiful but performed well too — and the entire collaboration process was smooth."',
    name: 'Lisa Kuroda',
    role: 'Founder, Studio Analog',
    img: 'https://framerusercontent.com/images/nfHihiND3hFVe8PsrYPUstbAcQ.jpg',
  },
  {
    quote: '"Nova AI approaches every project with a deep sense of purpose. Their work is never just about the surface — it\'s about how each element functions, connects, and flows. They bring logic, sharpness, and confidence to every decision, and their build quality is exceptional."',
    name: 'Daniel Reyes',
    role: 'Director, Framehaus',
    img: 'https://framerusercontent.com/images/1NojF9yywMvqzHNbp79Nt0uTs.png',
  },
  {
    quote: '"Their ability to merge storytelling with clean interaction design is unmatched. Nova AI understands not just how things should look, but why they should look that way — and that insight came through in every part of the work."',
    name: 'Mei Tanaka',
    role: 'UX Designer, Nuro',
    img: 'https://framerusercontent.com/images/3I9yi7BmKFs7ilmb0ijcCQZSZfo.png',
  },
  {
    quote: '"Working with Nova AI was more than just hiring a designer — it felt like bringing on a creative partner who truly understood our goals. They took our raw ideas, added clarity, and transformed them into something that not only looked stunning but converted."',
    name: 'Julian Pierce',
    role: 'Director, Vektor Inc.',
    img: 'https://framerusercontent.com/images/CqJ0xMtE2jw5wg8qA63IwRzImN0.png',
  },
  {
    quote: '"Nova AI brings a rare balance of creativity and discipline. They\'re incredibly fast without ever sacrificing attention to detail. From early ideation to the final product, their process is intentional, their communication is clear."',
    name: 'Hana Samoto',
    role: 'CEO, Willow Studio',
    img: 'https://framerusercontent.com/images/vYFurv3Bhpru2Pn28GNGdN5WOk.png',
  },
];

function TestimonialCard({ t, delay }: { t: typeof testimonials[0]; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`border border-white/10 p-6 flex flex-col justify-between transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <p className="text-white/60 text-xs leading-relaxed mb-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>{t.quote}</p>
      <div>
        <div className="h-px bg-white/10 mb-4"></div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
              <img src={t.img} alt={t.name} className="w-full h-full object-cover object-top" />
            </div>
            <div>
              <p className="text-white text-xs font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>{t.name}</p>
              <p className="text-white/35 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>{t.role}</p>
            </div>
          </div>
          <img
            src="https://framerusercontent.com/images/liDp6RqOmZpoiyriU2da9i9ZRNM.png"
            alt="logo"
            className="h-5 opacity-25 object-contain"
          />
        </div>
      </div>
    </div>
  );
}

export default function PalmerTestimonials() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-black py-20">
      <PalmerSectionLabel
        left1="© Testimonials レビュー"
        left2="(WDX® — 07)"
        right="Real Feedback"
      />

      <div className="px-6">
        {/* Heading + CTA */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div className="overflow-hidden">
            <h2
              className={`text-white font-semibold transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 'clamp(36px, 5.5vw, 80px)',
                letterSpacing: '-0.04em',
                lineHeight: 1,
              }}
            >
              Testimonial© - Reviews
            </h2>
          </div>
          <a
            href="/contact"
            className="inline-flex items-center border border-white/30 text-white text-xs px-5 py-2.5 hover:bg-white hover:text-black transition-all duration-300 whitespace-nowrap self-start lg:self-end"
            style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.1em' }}
          >
            Get in touch
          </a>
        </div>

        {/* 2-col top row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <TestimonialCard t={testimonials[0]} delay={0} />
          <TestimonialCard t={testimonials[1]} delay={100} />
        </div>
        {/* 3-col bottom row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <TestimonialCard t={testimonials[2]} delay={0} />
          <TestimonialCard t={testimonials[3]} delay={100} />
          <TestimonialCard t={testimonials[4]} delay={200} />
        </div>
      </div>
    </section>
  );
}
